import React, { useEffect, useState } from "react";
import Cta from "@qlik/qlik/src/components/Cta";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import SingleIconCardWithShadow from "@qlik/qlik/src/components/Card/Single-Icon-Card-With-Shadow";
import Slider from "react-slick";
import { debounce } from "@qlik/qlik/src/lib/utils";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";

/**
 * Card List Mobile Slider component
 * @param {Object} data - The data for the IconCard
 * @returns {JSX.Element} - The IconCard component
 */

const CardListMobileSlider = ({ data }) => {
    const [sliderSettings, setSliderSettings] = useState(null);
    const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth <= 768);
    // Destructuring the data object to get the required properties
    const {
        id,
        customId,
        title,
        subtitle,
        description,
        cardItems = [],
        cta = [],
        additionalConfiguration,
        numberOfColumns,
    } = data ?? {};
    // Check if the first row should be rendered based on the data properties
    const shouldRenderFirstRow = title || subtitle || description?.description;

    // Get the class name for the section padding
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration, "");
    const { sectionColorMapping } = getColorClassName(data?.backgroundColor);

    const groupedCardItems = [];
    for (let i = 0; i < cardItems.length; i += 4) {
        groupedCardItems.push(cardItems.slice(i, i + 4));
    }

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(typeof window !== "undefined" && window.innerWidth <= 768);
        };

        const DEBOUNCE_THRESHOLD = 250;
        const debouncedResizeHandler = debounce(handleResize, DEBOUNCE_THRESHOLD);
        // Set up event listener for window resize
        window.addEventListener("resize", debouncedResizeHandler);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        setSliderSettings({
            dots: true,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: false,
            arrows: false,
        });
    }, [isMobile]);

    return (
        <section
            data-section
            id={customId ?? id}
            className={`py-12.5 lg:pt-30 lg:pb-20 scroll-mt-45 ${styles.mobileTitle} ${sectionSpacingClassName} ${sectionColorMapping.bgColor}`}
        >
            <div className="container">
                {shouldRenderFirstRow && (
                    <div className="row">
                        <div className="col-12 mb-5 md:mb-14.5">
                            {title && (
                                <h2
                                    className={`text-center mb-5 ${sectionColorMapping.primaryTextColor}`}
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                                />
                            )}
                            {subtitle && (
                                <p className={`body-big text-center mb-5 ${sectionColorMapping.secondaryTextColor}`}>
                                    {subtitle}
                                </p>
                            )}
                            {description?.description && (
                                <p className={`lg:body-big text-center mb-5 ${sectionColorMapping.secondaryTextColor}`}>
                                    {description?.description}
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {isMobile ? (
                <div>
                    <Slider {...sliderSettings} className={`qlikSlider ${styles.mobileSlider}`}>
                        {groupedCardItems.map((group, index) => (
                            <div className="container">
                                <div className="grid grid-cols-2 gap-3.75">
                                    {group.map((cardItem) => (
                                        <SingleIconCardWithShadow
                                            data={cardItem}
                                            isTitleLimit={cardItem.__typename === "ContentfulResource"}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </Slider>
                </div>
            ) : (
                <div className="container">
                    <div
                        className={`grid ${
                            numberOfColumns === null
                                ? "md:grid-cols-3"
                                : `md:grid-cols-2 lg:grid-cols-${numberOfColumns || 3}`
                        } md:gap-x-7.5 md:gap-y-2.5`}
                    >
                        {cardItems?.map((cardItem) => (
                            <div className="mb-5" key={cardItem.id}>
                                <SingleIconCardWithShadow
                                    data={cardItem}
                                    isTitleLimit={cardItem.__typename === "ContentfulResource"}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className="container">
                {cta && cta?.length > 0 && (
                    <div className="row pt-7.5 md:pt-15">
                        <div className="col-12 mb-2.5">
                            {cta?.map((ct, index) => (
                                <Cta
                                    key={ct.id}
                                    data={ct}
                                    className={`block mb-7.5 mx-auto ${index === 0 ? "" : "md:text-md text-center"}`}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default CardListMobileSlider;
